/**
 * Analytics utility functions powered by Mixpanel
 * Scalable analytics service for tracking user events and behavior
 *
 * @example
 * // Track a custom event
 * import { analytics, AnalyticsEvents } from './utils/analytics';
 *
 * analytics.track('button.click', { buttonName: 'Sign Up', location: 'header' });
 *
 * // Track page load (automatically tracked on route changes)
 * analytics.trackPageLoad('/about');
 *
 * // Identify a user
 * analytics.identify('user123', { email: 'user@example.com', plan: 'premium' });
 *
 * // Add new event types in AnalyticsEvents constant below
 */

import mixpanel from "mixpanel-browser";

// Analytics configuration
const MIXPANEL_TOKEN = "d09b7e7ed15b9a3b53b05f7f8ba1b061";

// Analytics event names (centralized for consistency)
export const AnalyticsEvents = {
  WEB_PAGE_LOAD: "web-page.load",
  WEB_CLICK: "web.click",
  // Add more events here as needed
  // FORM_SUBMIT: 'form.submit',
  // etc.
} as const;

// Type for analytics properties
export type AnalyticsProperties = Record<string, any>;

/**
 * Analytics service class - Singleton pattern for scalability
 */
class AnalyticsService {
  private initialized = false;
  private debug = false;

  /**
   * Initialize Mixpanel with configuration
   */
  init(debug = false): void {
    if (typeof window === "undefined") {
      return; // Skip initialization during SSR
    }

    if (this.initialized) {
      console.log("[Analytics] Already initialized");
      return;
    }

    this.debug = debug;

    try {
      console.log("[Analytics] Initializing Mixpanel...");
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: false, // Always disable debug in production
        track_pageview: false, // We'll handle page views manually
        persistence: "localStorage",
        api_host: "https://api-eu.mixpanel.com", // EU data residency
        ignore_dnt: true,
        loaded: (mixpanel) => {
          console.log("[Analytics] Mixpanel loaded callback fired");
          this.initialized = true;
        },
      });

      // Set initialized flag immediately (will be confirmed by loaded callback)
      this.initialized = true;
      console.log(
        "[Analytics] Initialization complete, initialized:",
        this.initialized
      );
    } catch (error) {
      console.error("[Analytics] Failed to initialize:", error);
      this.initialized = false;
    }
  }

  /**
   * Track a custom event
   */
  track(eventName: string, properties?: AnalyticsProperties): void {
    if (typeof window === "undefined") {
      console.log("[Analytics] Skipping track (SSR):", eventName);
      return;
    }

    if (!this.initialized) {
      console.warn("[Analytics] Not initialized, cannot track:", eventName);
      return;
    }

    try {
      console.log("[Analytics] Tracking event:", eventName, properties);
      mixpanel.track(eventName, properties);
    } catch (error) {
      console.error("[Analytics] Failed to track event:", error);
    }
  }

  /**
   * Track page view/load
   */
  trackPageLoad(url?: string, properties?: AnalyticsProperties): void {
    const pageUrl =
      url || (typeof window !== "undefined" ? window.location.pathname : "");

    this.track(AnalyticsEvents.WEB_PAGE_LOAD, {
      url: pageUrl,
      title: typeof document !== "undefined" ? document.title : "",
      referrer: typeof document !== "undefined" ? document.referrer : "",
      hostname: typeof window !== "undefined" ? window.location.hostname : "",
      environment:
        typeof window !== "undefined" &&
        window.location.hostname === "localhost"
          ? "development"
          : "production",
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }

  /**
   * Track click events
   */
  trackClick(
    elementType: string,
    elementLabel: string,
    properties?: AnalyticsProperties
  ): void {
    this.track(AnalyticsEvents.WEB_CLICK, {
      element_type: elementType,
      element_label: elementLabel,
      page_url: typeof window !== "undefined" ? window.location.pathname : "",
      timestamp: new Date().toISOString(),
      ...properties,
    });
  }

  /**
   * Identify a user (for future use)
   */
  identify(userId: string, properties?: AnalyticsProperties): void {
    if (typeof window === "undefined" || !this.initialized) {
      return;
    }

    try {
      mixpanel.identify(userId);

      if (properties) {
        mixpanel.people.set(properties);
      }
    } catch (error) {
      console.error("Failed to identify user:", error);
    }
  }

  /**
   * Set user properties (for future use)
   */
  setUserProperties(properties: AnalyticsProperties): void {
    if (typeof window === "undefined" || !this.initialized) {
      return;
    }

    try {
      mixpanel.people.set(properties);
    } catch (error) {
      console.error("Failed to set user properties:", error);
    }
  }

  /**
   * Reset analytics state (useful for logout)
   */
  reset(): void {
    if (typeof window === "undefined" || !this.initialized) {
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      console.error("Failed to reset analytics:", error);
    }
  }

  /**
   * Check if analytics is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }
}

// Export singleton instance
export const analytics = new AnalyticsService();

// Legacy function exports for backward compatibility
export const trackPageView = (
  url: string,
  properties?: AnalyticsProperties
): void => {
  analytics.trackPageLoad(url, properties);
};

export const trackEvent = (
  eventName: string,
  properties?: AnalyticsProperties
): void => {
  analytics.track(eventName, properties);
};
